import React, { useState } from 'react';
import { Box, Paper, Select, MenuItem, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { langItems } from '../i18n';
import { setLanguage, LangKeys, setFlowToken, setPhone } from '../store/mainSlice';
import { useAppSelector } from '../store/hooks';
import { useA11yTokens } from '../hooks/useA11yTokens';
import { useIsDesktopDevice } from '../hooks/useIsDesktopDevice';
import { theme4dTokens as baseTokens } from '../theme4d';
import yitLogo from '../assets/yitLogo.png';
import languageIcon from '../assets/languageIcon.png';
import { APP_VERSION_NUMBER } from '../enums';
import { supportWhatsappNumber } from '../constants';
import A11yDrawer from './A11yDrawer';

interface FlowLayoutProps {
    children: React.ReactNode;
}

// Screens where the browser back button should force a return to Home instead of the
// browser's default one-step-back behavior. `/offer` is intentionally excluded — back
// from there should land on `/companies` as normal.
const BACK_TO_HOME_PATHS = ['/otp', '/companies', '/offer-success', '/error'];

const FlowLayout: React.FC<FlowLayoutProps> = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const language = useAppSelector((state) => state.main.language);
    const packageInfo = useAppSelector((state) => state.main.packageInfo);
    const originalQuery = useAppSelector((state) => state.main.originalQuery);
    const a11yContrast = useAppSelector((state) => state.main.a11yContrast);
    const isHighContrast = a11yContrast === 'high';
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const [a11yDrawerOpen, setA11yDrawerOpen] = useState(false);

    // Pressing the browser back button on select screens should land back on Home rather
    // than stepping one screen back through the flow (see BACK_TO_HOME_PATHS above).
    React.useEffect(() => {
        if (!BACK_TO_HOME_PATHS.includes(location.pathname)) return;
        const onPopState = () => {
            dispatch(setPhone(''));
            dispatch(setFlowToken(''));            
            navigate(originalQuery ? `/?${originalQuery}` : '/', { replace: true });
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [navigate, location.pathname, originalQuery, dispatch]);

    const langSelect = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box component="img" src={languageIcon} alt="" sx={{ width: 18, height: 18 }} />
            <Select
                value={language}
                onChange={(e) => dispatch(setLanguage(e.target.value as LangKeys))}
                variant="standard"
                disableUnderline
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    minWidth: '92px',
                    height: '34px',
                    bgcolor: theme4dTokens.surface,
                    border: `1px solid ${theme4dTokens.border}`,
                    borderRadius: '8px',
                    px: '8px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: theme4dTokens.text,
                    '& .MuiSelect-select': { py: 0, pr: '4px', pl: 0, textAlign: 'right', flex: 1, display: 'flex', alignItems: 'center' },
                    '& .MuiSelect-icon': { position: 'static', mx: '4px' },
                }}
            >
                {langItems.map((lg) => (
                    <MenuItem key={lg.code} value={lg.code}>
                        {lg.name}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );

    const a11yButton = (
        <>
            <Box
                component="button"
                type="button"
                aria-label={t('a11y.label')}
                onClick={() => setA11yDrawerOpen(true)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    border: `1px solid ${theme4dTokens.border}`,
                    bgcolor: theme4dTokens.surface,
                    color: theme4dTokens.text,
                    cursor: 'pointer',
                    p: 0,
                }}
            >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="3.8" r="2" />
                    <path d="M20 7.5a1 1 0 0 1-.7 1.2l-4.3 1.1v3.1l1.6 6.1a1 1 0 0 1-1.9.5L13 15h-2l-1.7 4.5a1 1 0 0 1-1.9-.5L9 12.9V9.8L4.7 8.7A1 1 0 0 1 5.3 6.8L11 8.3h2l5.7-1.5a1 1 0 0 1 1.3.7Z" />
                </svg>
            </Box>
            <A11yDrawer open={a11yDrawerOpen} onClose={() => setA11yDrawerOpen(false)} />
        </>
    );

    const bodyCard = (
        <Box
            sx={{
                p: '22px 20px 26px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                ...(!isDesktop && { flex: 1 }),
                ...(isDesktop && {
                    width: '100%',
                    maxWidth: '860px',
                    bgcolor: theme4dTokens.surface,
                    borderRadius: '14px',
                    boxShadow: '0 10px 40px rgba(44,62,80,.12)',
                    border: `1px solid ${theme4dTokens.border}`,
                    p: '44px 56px',
                }),
            }}
        >
            {packageInfo?.promoHeader && (
                <Typography
                    sx={{
                        alignSelf: 'flex-start',
                        boxSizing: 'border-box',
                        maxWidth: '100%',
                        bgcolor: baseTokens.badgeBg,
                        color: baseTokens.badgeText,
                        fontSize: 12.5,
                        fontWeight: 700,
                        px: '13px',
                        py: '6px',
                        borderRadius: '999px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                    }}
                >
                    {packageInfo.promoHeader}
                </Typography>
            )}
            {children}
        </Box>
    );

    if (isDesktop) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: theme4dTokens.surface,
                }}
            >
                {/* top strip */}
                <Box sx={{ height: '4px', bgcolor: theme4dTokens.accent, flexShrink: 0 }} />

                {/* web header: logo + controls, full viewport width */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '32px',
                        py: '16px',
                        bgcolor: theme4dTokens.surface,
                        borderBottom: `1px solid ${theme4dTokens.border}`,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        component="a"
                        href="https://y-it.co.il/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', flexShrink: 0 }}
                    >
                        <Box
                            component="img"
                            src={yitLogo}
                            alt="YIT"
                            sx={{ height: 40, width: 'auto', flexShrink: 0 }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        {langSelect}
                        {a11yButton}
                    </Box>
                </Box>

                {/* stage: page background from design + centered content card, fills remaining height */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        p: '44px 24px',
                        bgcolor: isHighContrast ? theme4dTokens.bg : '#fbfbfc',
                        backgroundImage: isHighContrast
                            ? 'none'
                            : 'repeating-linear-gradient(115deg, transparent 0 58px, #eef0f3 58px 61px), repeating-linear-gradient(24deg, transparent 0 78px, #eef1f4 78px 81px)',
                    }}
                >
                    {bodyCard}
                </Box>

                {/* footer, full viewport width */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        alignItems: 'center',
                        px: '32px',
                        py: '14px',
                        bgcolor: theme4dTokens.surface,
                        borderTop: `1px solid ${theme4dTokens.border}`,
                        fontSize: '12px',
                        color: theme4dTokens.muted,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        component="a"
                        href={`https://wa.me/${supportWhatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            textAlign: 'right',
                            color: theme4dTokens.accent,
                            textDecoration: 'underline',
                            fontWeight: 600,
                        }}
                    >
                        {t('footer.contactSupport')}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>{t('footer.poweredBy')}</Box>
                    <Box sx={{ textAlign: 'left', direction: 'ltr' }}>Ver {APP_VERSION_NUMBER}</Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                bgcolor: isHighContrast ? theme4dTokens.bg : '#f2f4f7',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    minHeight: '100dvh',
                    borderRadius: 0,
                    boxShadow: 'none',
                    bgcolor: theme4dTokens.bg,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* t.header: logo + language select */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '20px',
                        py: '15px',
                        borderBottom: `1px solid ${theme4dTokens.border}`,
                        flexShrink: 0,
                        bgcolor: theme4dTokens.bg,
                    }}
                >
                    <Box
                        component="a"
                        href="https://y-it.co.il/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', flexShrink: 0 }}
                    >
                        <Box
                            component="img"
                            src={yitLogo}
                            alt="YIT"
                            sx={{ height: 36, width: 'auto', flexShrink: 0 }}
                        />
                    </Box>
                    {langSelect}
                </Box>

                {/* t.body */}
                {bodyCard}
            </Paper>
        </Box>
    );
};

export default FlowLayout;
